
import { useState, useEffect, useCallback, useRef } from 'react';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { NotificationItem } from './NotificationItem';
import { Button } from '@/components/ui/button';

export interface Notification {
  id: string;
  user_id: string;
  title: string;
  content: string;
  category: 'order' | 'message' | 'review' | 'payment' | 'system';
  priority: 1 | 2 | 3;
  is_read: boolean;
  created_at: string;
  action_url?: string;
  image_url?: string | null;
}

const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const subscription = useRef<ReturnType<typeof supabase.channel> | null>(null);
  
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5)
        .returns<Notification[]>();
        
      if (error) throw error;
      
      setNotifications(data);
      setUnreadCount(data.filter(n => !n.is_read).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);
  
  useEffect(() => {
    if (!user) return;
    
    fetchNotifications();
    
    const setupSubscription = async () => {
      if (subscription.current) {
        subscription.current.unsubscribe();
      }
      
      subscription.current = supabase
        .channel('notifications-channel')
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        }, (payload) => {
          console.log('Notification change received:', payload);
          
          if (payload.eventType === 'INSERT') {
            setNotifications(prev => [payload.new as Notification, ...prev].slice(0, 5));
            setUnreadCount(prev => prev + 1);
          } else if (payload.eventType === 'UPDATE') {
            setNotifications(prev => 
              prev.map(n => n.id === payload.new.id ? payload.new as Notification : n)
            );
            setNotifications(prev => {
              const unreadCount = prev.filter(n => !n.is_read).length;
              setUnreadCount(unreadCount);
              return prev;
            });
          } else if (payload.eventType === 'DELETE') {
            setNotifications(prev => 
              prev.filter(n => n.id !== payload.old.id)
            );
            setNotifications(prev => {
              const unreadCount = prev.filter(n => !n.is_read).length;
              setUnreadCount(unreadCount);
              return prev;
            });
          }
        })
        .subscribe();
    };
    
    setupSubscription();
    
    return () => {
      if (subscription.current) {
        subscription.current.unsubscribe();
      }
    };
  }, [user, fetchNotifications]);
  
  const handleMarkAllAsRead = async () => {
    if (!user || notifications.length === 0) return;
    
    try {
      await supabase.rpc('mark_notifications_as_read');
      
      setNotifications(prev => 
        prev.map(n => ({ ...n, is_read: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };
  
  const handleMarkAsRead = async (notificationId: string) => {
    if (!user) return;
    
    try {
      await supabase.rpc('mark_notifications_as_read', {
        p_notification_ids: [notificationId]
      });
      
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };
  
  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      handleMarkAsRead(notification.id);
    }
    
    setIsOpen(false);
    
    if (notification.action_url) {
      navigate(notification.action_url);
    }
  };
  
  const handleViewAll = () => {
    setIsOpen(false);
    navigate('/notifications');
  };
  
  if (!user) return null;
  
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button 
          className="flex max-w-[480px] cursor-pointer items-center justify-center overflow-hidden rounded-full h-10 w-10 transition-colors relative bg-card text-muted-foreground hover:bg-muted hover:text-primary"
          aria-label="Notifications"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-destructive-foreground transform translate-x-1/2 -translate-y-1/2 bg-destructive rounded-full">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-80 p-0 overflow-hidden shadow-lg rounded-lg bg-background border-border" 
        align="end"
        forceMount
      >
        <div 
          className="px-4 py-3 border-b border-border bg-muted/50" 
        >
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-foreground">Notifications</h3>
            {unreadCount > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleMarkAllAsRead}
                className="text-xs hover:bg-primary/10 h-auto p-1 text-muted-foreground hover:text-foreground"
              >
                Mark all as read
              </Button>
            )}
          </div>
        </div>
        <div className="max-h-80 overflow-auto bg-background">
          {loading ? (
            <div className="p-4 text-center text-muted-foreground">
              Loading notifications...
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              No notifications
            </div>
          ) : (
            <div>
              {notifications.map((notification) => (
                <div key={notification.id} className="relative group hover:bg-muted/50 transition-colors">
                  <NotificationItem 
                    notification={notification}
                    onClick={() => handleNotificationClick(notification)}
                    onMarkAsRead={handleMarkAsRead}
                    compact={true}
                  />
                  {!notification.is_read && (
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsRead(notification.id);
                        }}
                        className="h-6 px-2 text-xs hover:bg-primary/10 text-muted-foreground hover:text-foreground"
                      >
                        Mark as read
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="px-2 py-1.5 border-t border-border">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleViewAll}
            className="w-full hover:bg-primary/10 rounded-md px-3 py-2 transition-colors text-foreground"
          >
            View all notifications
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
